import { useState, useEffect, useCallback, memo, useMemo } from "react";
import Select, { MultiValue, StylesConfig, components } from "react-select";
import { Option } from "../../interface/Option";
import { MedicalCodesAxiosInstance } from "../../Network/MedicalCodesAxiosInstance";

interface ListofIcdMedicalCodeProps {
  onChange: (selected: Option[]) => void;
  clear?: boolean;
  defaultValue?: Option[];
  description?: string;
  qaIdPrefix?: string;
}

const DEFAULT_QA_PREFIX = "list-of-icd-medical-code";

const qaSafe = (value: string | number | undefined | null) =>
  String(value ?? "unknown")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");

const buildQaId = (prefix: string, suffix: string | number) =>
  `${qaSafe(prefix)}-${qaSafe(suffix)}`;

const qaAttrs = (prefix: string, suffix: string | number) => {
  const id = buildQaId(prefix, suffix);
  return {
    id,
    "data-testid": id,
    "data-qa": id,
  };
};

const ListofIcdMedicalCode = memo(
  ({
    onChange,
    clear,
    defaultValue,
    description,
    qaIdPrefix = DEFAULT_QA_PREFIX,
  }: ListofIcdMedicalCodeProps) => {
    const [options, setOptions] = useState<Option[]>([]);
    const [selectedOptions, setSelectedOptions] = useState<Option[]>(defaultValue || []);
    const [loading, setLoading] = useState(false);
    const [inputValue, setInputValue] = useState<string>("");
    const cache = useMemo(() => new Map<string, Option[]>(), []);

    const selectQaId = buildQaId(qaIdPrefix, "select");
    const inputQaId = buildQaId(qaIdPrefix, "input");
    const loadOptions = useCallback(async () => {
      if (inputValue.trim().length < 3 || loading) {
        setOptions([]);
        return;
      }

      const cacheKey = `${description}-${inputValue}`;
      if (cache.has(cacheKey)) {
        setOptions(cache.get(cacheKey)!);
        return;
      }

      setLoading(true);
      try {
        const response = await MedicalCodesAxiosInstance.get(`getIcd/${description}`, {
          params: { name: inputValue, limit: 10 },
        });
        const data = response.data?.result || [];
        console.log("API Response:", data);

        const formattedOptions: Option[] = data
          .map((item: any) => ({
            value: item.icd.toString(),
            label: `${item.icd} - ${item.description}`,
          }))
          .filter((option: Option) => option.label.toLowerCase().includes(inputValue.toLowerCase()))
          .sort((a: Option, b: Option) => {
            const aStartsWith = a.label.toLowerCase().startsWith(inputValue.toLowerCase());
            const bStartsWith = b.label.toLowerCase().startsWith(inputValue.toLowerCase());
            if (aStartsWith && !bStartsWith) return -1;
            if (!aStartsWith && bStartsWith) return 1;
            return a.label.localeCompare(b.label);
          })
          .slice(0, 10);

        if (cache.size > 100) cache.clear();
        cache.set(cacheKey, formattedOptions);
        setOptions(formattedOptions);
      } catch (error) {
        console.error("Error loading options:", error);
        setOptions([]);
      } finally {
        setLoading(false);
      }
    }, [inputValue, description, cache]);

    useEffect(() => {
      const debounceTimeout = setTimeout(() => {
        loadOptions();
      }, 100);
      return () => clearTimeout(debounceTimeout);
    }, [inputValue, loadOptions]);

    const handleChange = useCallback(
      (selected: MultiValue<Option>) => {
        const selectedArray = selected as Option[];
        setSelectedOptions(selectedArray);
        onChange(selectedArray);
      },
      [onChange]
    );

    useEffect(() => {
      if (clear) setSelectedOptions([]);
    }, [clear]);

    useEffect(() => {
      if (defaultValue && JSON.stringify(defaultValue) !== JSON.stringify(selectedOptions)) {
        setSelectedOptions(defaultValue);
      }
    }, [defaultValue]);

    const customStyles: StylesConfig<Option, true> = useMemo(
      () => ({
        multiValue: (styles) => ({
          ...styles,
          backgroundColor: "#007BFF",
          color: "#FFFFFF",
          borderRadius: "5px",
        }),
        multiValueLabel: (styles) => ({ ...styles, color: "#FFFFFF" }),
        multiValueRemove: (styles) => ({
          ...styles,
          color: "#FFFFFF",
          ":hover": { backgroundColor: "#0056b3", color: "#FFFFFF" },
        }),
        option: (styles, { isSelected }) => ({
          ...styles,
          backgroundColor: isSelected ? "#007BFF" : "#FFFFFF",
          color: isSelected ? "#FFFFFF" : "#000000",
          ":hover": { backgroundColor: "#0056b3", color: "#FFFFFF" },
        }),
      }),
      []
    );

    return (
      <div {...qaAttrs(qaIdPrefix, "wrapper")}>
        <Select
          isMulti
          inputId={inputQaId}
          instanceId={selectQaId}
          name={selectQaId}
          classNamePrefix={selectQaId}
          className={selectQaId}
          aria-label="ICD medical code selector"
          data-testid={selectQaId}
          data-qa={selectQaId}
          value={selectedOptions}
          onChange={handleChange}
          options={options}
          isLoading={loading}
          onInputChange={setInputValue}
          placeholder="Type at least 3 letters to search..."
          styles={customStyles}
          noOptionsMessage={() =>
            loading ? "Loading..." : inputValue.length < 3 ? "Type at least 3 letters..." : "No options found"
          }
          maxMenuHeight={150}
          components={{
            Menu: (props) => (
              <div {...qaAttrs(qaIdPrefix, "menu")}>
                <components.Menu {...props} />
              </div>
            ),
            Option: (props) => (
              <div
                {...qaAttrs(qaIdPrefix, `option-${qaSafe(String(props.data.value))}`)}
                data-option-label={props.data.label}
                data-option-value={props.data.value}
              >
                <components.Option {...props} />
              </div>
            ),
            MultiValue: (props) => (
              <div
                {...qaAttrs(qaIdPrefix, `selected-${qaSafe(String(props.data.value))}`)}
                data-selected-label={props.data.label}
                data-selected-value={props.data.value}
              >
                <components.MultiValue {...props} />
              </div>
            ),
          }}
        />
        <span
          {...qaAttrs(qaIdPrefix, "loading-state")}
          data-loading={loading ? "true" : "false"}
          style={{ display: "none" }}
        >
          {loading ? "Loading" : "Idle"}
        </span>
        <span
          {...qaAttrs(qaIdPrefix, "selected-count")}
          data-count={selectedOptions.length}
          style={{ display: "none" }}
        >
          {selectedOptions.length}
        </span>
      </div>
    );
  }
);

ListofIcdMedicalCode.displayName = "ListofIcdMedicalCode";
export default ListofIcdMedicalCode;
